import type {
  ExpenseSummary,
  ClientApprovalStatus,
  QuestionnaireStatus,
  RsvpResponseValue,
  RoomShape,
  ClientTask,
  CreateClientTaskInput,
  UpdateClientTaskInput,
  ClientExpensePaymentMethods,
  VendorPortalBooking,
  VendorPortalItemLine,
  ResolvedDecorValues,
} from '@peles/shared';
import { apiClient } from './client';
import { uploadFileToS3 } from './photos';

export interface ClientEvent {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
  status: string;
  eventTypeName: string | null;
  venueName: string | null;
  guestCount: number | null;
  budgetVisibility: string;
  clientRole: string;
  permissions: ClientEventPermissions;
}

export interface ClientEventPermissions {
  canApprovePlan: boolean;
  canApproveInvoice: boolean;
  canMakePayments: boolean;
  canMakeChanges: boolean;
  /** Allows the client to print (floor plan, timeline). */
  canPrint: boolean;
  /** Allows the client to manage invites & RSVPs in the portal. */
  canManageInvites: boolean;
  canManageGuests: boolean;
  /** The client's own tasks: see them, add their own, tick them off. */
  canManageTasks: boolean;
  /** Seat guests on the event's seating plan from the portal (not while it is locked). */
  canManageSeating: boolean;
  /** Start and join conversations about this event. */
  canMessage: boolean;
  /** Be in a conversation with the event's vendors directly. */
  canMessageVendors: boolean;
}

export interface ClientExpenseItem {
  id: string;
  eventId: string;
  vendorCategoryName: string | null;
  itemName: string | null;
  vendorName: string | null;
  price: number | null;
  /** What the client has paid toward this expense (null when the budget is not fully visible). */
  paid: number | null;
  /** What the client still has to provide for this expense (null when the budget is not fully visible). */
  openBalance: number | null;
  /** True when the open balance is money owed back to the producer, who fronted this expense. */
  owedToProducer: boolean;
  /** True when the line is the producer's own invoice — owed to them, but not a reimbursement. */
  producerInvoice: boolean;
  statusName: string | null;
  statusColor: string | null;
  clientApproval: ClientApprovalStatus;
  /** Number of files attached to the expense (imported documents + attachments). */
  documentCount: number;
  /** Ways to pay this line's payee — its vendor, or the office when owed to the producer. */
  paymentMethodCount: number;
}

/** A read-only file attached to one of the client's event expenses. */
export interface ClientExpenseDocument {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number | null;
  type: string | null;
  createdAt: string;
  /** Presigned S3 URL, or the external link for link-only attachments. */
  downloadUrl?: string;
}

export interface VendorEvent {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventTypeName: string | null;
  /** The event is cancelled: no tasks are added to it. */
  cancelled?: boolean;
  /** Where the event is held — the venue's name and address in one line. */
  venue: { name: string; address: string | null } | null;
  /**
   * The caller's booking on the event — one per vendor they work for whose
   * link shares anything — read-only, money only with that vendor's own
   * expenses grant.
   */
  bookings: VendorPortalBooking[];
  vendorName: string;
  categories: string[];
  totalQuoted: number;
  totalPaid: number;
  status: string;
  /** The office has turned conversations about this event on for this vendor. */
  canMessage: boolean;
  /**
   * What the office shared of this event (`@peles/shared` `utils/vendorPortal.ts`).
   * The money above is nought without `expenses`.
   */
  access: VendorEventAccess;
}

export interface VendorEventAccess {
  expenses: boolean;
  timeline: boolean;
  /** The items of their own booked lines and the files on them, read only. */
  items: boolean;
  /** Their tasks on the event: some were assigned to them, or they may add their own. */
  tasks: boolean;
  /** The office lets them add tasks of their own. */
  addTasks: boolean;
  /** Folders of the event's files, each opened from that folder down. */
  folders: { nodeId: string; access: 'read' | 'upload'; name: string }[];
  floorPlans: { floorPlanId: string; name: string; guestNames: boolean }[];
}

export interface VendorExpenseItem {
  id: string;
  eventId: string;
  vendorCategoryName: string | null;
  itemName: string | null;
  price: number;
  statusName: string | null;
  statusColor: string | null;
  dueDate: string | null;
  comments: string | null;
  totalPaid: number;
  balance: number;
  payments: Array<{
    id: string;
    amount: number;
    paymentMethodName: string | null;
    paidAt: string;
    notes: string | null;
  }>;
}

export interface VendorTimelineEntry {
  id: string;
  time: string | null;
  title: string;
  location: string | null;
  vendorNote: string | null;
  notes: string | null;
  sectionName: string | null;
}

export interface VendorProfile {
  id: string;
  businessName: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
}

// Client Portal
export async function getClientEvents(): Promise<ClientEvent[]> {
  const { data } = await apiClient.get<ClientEvent[]>('/portal/client/events');
  return data;
}

export async function getClientExpenseSummary(eventId: string): Promise<ExpenseSummary> {
  const { data } = await apiClient.get<ExpenseSummary>(
    `/portal/client/events/${eventId}/expense-summary`,
  );
  return data;
}

export async function getClientExpenseItems(eventId: string): Promise<ClientExpenseItem[]> {
  const { data } = await apiClient.get<ClientExpenseItem[]>(
    `/portal/client/events/${eventId}/expenses`,
  );
  return data;
}

/** How the client pays one expense line: who to, how, and the open balance. */
export async function getClientExpensePaymentMethods(
  eventId: string,
  expenseItemId: string,
): Promise<ClientExpensePaymentMethods> {
  const { data } = await apiClient.get<ClientExpensePaymentMethods>(
    `/portal/client/events/${eventId}/expenses/${expenseItemId}/payment-methods`,
  );
  return data;
}

export async function getClientExpenseDocuments(
  eventId: string,
  expenseItemId: string,
): Promise<ClientExpenseDocument[]> {
  const { data } = await apiClient.get<
    Array<
      Omit<ClientExpenseDocument, 'fileName' | 'mimeType' | 'downloadUrl'> & {
        fileName: string | null;
        mimeType: string | null;
        downloadUrl: string | null;
        externalUrl: string | null;
      }
    >
  >(`/portal/client/events/${eventId}/expenses/${expenseItemId}/documents`);

  // Attachments may be link-only (no S3 object) and carry no file name or mime
  // type — normalize so the preview components can rely on both.
  return data.map((doc) => ({
    id: doc.id,
    fileName: doc.fileName || 'Document',
    mimeType: doc.mimeType || '',
    fileSizeBytes: doc.fileSizeBytes,
    type: doc.type,
    createdAt: doc.createdAt,
    downloadUrl: doc.downloadUrl || doc.externalUrl || undefined,
  }));
}

export async function updateClientApproval(
  expenseItemId: string,
  clientApproval: ClientApprovalStatus,
): Promise<{ id: string; clientApproval: ClientApprovalStatus }> {
  const { data } = await apiClient.patch<{ id: string; clientApproval: ClientApprovalStatus }>(
    `/portal/client/expenses/${expenseItemId}/approval`,
    { clientApproval },
  );
  return data;
}

// Vendor Portal
export async function getVendorEvents(): Promise<VendorEvent[]> {
  const { data } = await apiClient.get<VendorEvent[]>('/portal/vendor/events');
  return data;
}

export async function getVendorEventExpenses(eventId: string): Promise<VendorExpenseItem[]> {
  const { data } = await apiClient.get<VendorExpenseItem[]>(
    `/portal/vendor/events/${eventId}/expenses`,
  );
  return data;
}

/** The items of the vendor's own booked lines, with their files — read only. */
export async function getVendorEventItems(eventId: string): Promise<VendorPortalItemLine[]> {
  const { data } = await apiClient.get<VendorPortalItemLine[]>(
    `/portal/vendor/events/${eventId}/items`,
  );
  return data;
}

export async function getVendorEventTimeline(eventId: string): Promise<VendorTimelineEntry[]> {
  const { data } = await apiClient.get<VendorTimelineEntry[]>(
    `/portal/vendor/events/${eventId}/timeline`,
  );
  return data;
}

export async function getVendorProfile(): Promise<VendorProfile> {
  const { data } = await apiClient.get<VendorProfile>('/portal/vendor/profile');
  return data;
}

export async function updateVendorProfile(
  updates: Partial<Pick<VendorProfile, 'businessName' | 'phone' | 'email' | 'website'>>,
): Promise<VendorProfile> {
  const { data } = await apiClient.patch<VendorProfile>('/portal/vendor/profile', updates);
  return data;
}

/**
 * Upload a quote or invoice from the vendor portal: presign, put the file in
 * S3, then file it on one of the vendor's expenses. The server checks that the
 * key is its own presign's, that the file arrived, and that the expense is the
 * vendor's.
 */
export async function uploadVendorDocument(
  eventId: string,
  doc: { file: File; documentType: 'quote' | 'invoice'; expenseItemId: string },
): Promise<{ id: string; fileName: string; documentType: string }> {
  // The server decides the type the file is stored with (the browser may report
  // none), signs the URL for it, and the upload must carry exactly that type.
  const { data: presign } = await apiClient.post<{
    uploadUrl: string;
    s3Key: string;
    mimeType: string;
  }>(`/portal/vendor/events/${eventId}/documents/upload-url`, {
    fileName: doc.file.name,
    mimeType: doc.file.type || 'application/octet-stream',
  });
  const { mimeType } = presign;
  await uploadFileToS3(presign.uploadUrl, doc.file, undefined, mimeType);
  const { data } = await apiClient.post<{ id: string; fileName: string; documentType: string }>(
    `/portal/vendor/events/${eventId}/documents`,
    {
      s3Key: presign.s3Key,
      fileName: doc.file.name,
      mimeType,
      documentType: doc.documentType,
      expenseItemId: doc.expenseItemId,
    },
  );
  return data;
}

export interface ClientEventDetail {
  id: string;
  name: string;
  startAt: string;
  endAt: string | null;
  status: string;
  guestCount: number | null;
  notes: string | null;
  budgetVisibility: string;
  eventTypeName: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  /** Presigned URL of the event's own logo, when it has one. */
  logoUrl?: string | null;
  /** The requesting client's own per-event permissions. */
  permissions: ClientEventPermissions;
}

export interface ClientGuest {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  rsvpStatus: string | null;
  tableNumber: number | null;
  mealPreference: string | null;
  notes: string | null;
  maleCount: number;
  femaleCount: number;
  kidCount: number;
  familyGroup: string | null;
  /** Normalized RSVP answer (YES/NO/MAYBE/PENDING), resolved server-side. */
  rsvpResponse: RsvpResponseValue;
  /** Actual attendants — zero unless the guest is attending. */
  attendingAdults: number;
  attendingKids: number;
  attendingTotal: number;
}

export interface ClientFloorPlanObject {
  id: string;
  objectType: string;
  tableNumber: number | null;
  label: string | null;
  x: number;
  y: number;
  widthFt: number | null;
  heightFt: number | null;
  rotation: number;
  color: string;
  opacity: number;
  seats: number | null;
  sortOrder: number;
  config: Record<string, unknown> | null;
}

export interface ClientFloorPlan {
  id: string;
  name: string;
  /** The part of the evening the plan lays out, named as Settings names it. */
  eveningPart: { value: string; label: string } | null;
  /** When that part begins, `HH:MM` on the 24-hour clock. */
  startTime: string | null;
  /** The look the 3D view draws: the event's, with this plan's changes. */
  decor?: ResolvedDecorValues;
  roomName: string | null;
  /**
   * The room's shape, when the plan is attached to one. The flat canvas sizes
   * itself from the objects and never drew the room; the 3D view draws its
   * floor and walls, and without this a room that is not a rectangle would be
   * shown to the client as one.
   */
  room: {
    name: string;
    widthFt: number;
    heightFt: number;
    perimeterPoints: Array<{ x: number; y: number }> | null;
    /** Its drawn walls, doors and fixed features (never the traced drawing). */
    shape?: RoomShape | null;
  } | null;
  objects: ClientFloorPlanObject[];
}

export interface ClientPhoto {
  id: string;
  fileName: string;
  s3Key: string;
  takenAt: string | null;
  caption: string | null;
}

export interface ClientTimelineEntry {
  id: string;
  time: string | null;
  title: string;
  location: string | null;
  notes: string | null;
}

export interface ClientTimelineSection {
  id: string;
  name: string;
  entries: ClientTimelineEntry[];
}

export interface ClientTimeline {
  id: string;
  name: string;
  description: string | null;
  sections: ClientTimelineSection[];
}

export async function getClientEventDetail(eventId: string): Promise<ClientEventDetail> {
  const { data } = await apiClient.get<ClientEventDetail>(
    `/portal/client/events/${eventId}/detail`,
  );
  return data;
}

/** Fields a client may set on a guest — seating stays with the producer. */
export interface PortalGuestInput {
  firstName: string;
  lastName?: string;
  familyGroup?: string;
  side?: string;
  phone?: string;
  email?: string;
  maleCount?: number;
  femaleCount?: number;
  kidCount?: number;
  notes?: string;
}

export async function createClientEventGuest(
  eventId: string,
  input: PortalGuestInput,
): Promise<ClientGuest> {
  const { data } = await apiClient.post<ClientGuest>(
    `/portal/client/events/${eventId}/guests`,
    input,
  );
  return data;
}

export async function updateClientEventGuest(
  eventId: string,
  guestId: string,
  updates: Partial<PortalGuestInput>,
): Promise<ClientGuest> {
  const { data } = await apiClient.patch<ClientGuest>(
    `/portal/client/events/${eventId}/guests/${guestId}`,
    updates,
  );
  return data;
}

export async function getClientEventGuests(eventId: string): Promise<ClientGuest[]> {
  const { data } = await apiClient.get<ClientGuest[]>(`/portal/client/events/${eventId}/guests`);
  return data;
}

/**
 * A floor plan the office shared with a vendor: as a client sees it, and —
 * only with the guest-names grant — who sits at each table, by the table's
 * `config.uid`. Without it the tables carry no labels.
 */
export interface VendorFloorPlan extends ClientFloorPlan {
  guestNames: boolean;
  seating: { tableUid: string; guests: string[] }[];
}

export async function getVendorEventFloorPlans(eventId: string): Promise<VendorFloorPlan[]> {
  const { data } = await apiClient.get<VendorFloorPlan[]>(
    `/portal/vendor/events/${eventId}/floor-plans`,
  );
  return data;
}

export async function getClientEventFloorPlans(eventId: string): Promise<ClientFloorPlan[]> {
  const { data } = await apiClient.get<ClientFloorPlan[]>(
    `/portal/client/events/${eventId}/floor-plans`,
  );
  return data;
}

export async function getClientEventPhotos(eventId: string): Promise<ClientPhoto[]> {
  const { data } = await apiClient.get<ClientPhoto[]>(`/portal/client/events/${eventId}/photos`);
  return data;
}

export async function getClientEventTimeline(eventId: string): Promise<ClientTimeline[]> {
  const { data } = await apiClient.get<ClientTimeline[]>(
    `/portal/client/events/${eventId}/timeline`,
  );
  return data;
}

// ─── Portal tasks — a client's or vendor's own ─────────────────────────────

/** Which portal a task is read through: the client's or the vendor's. */
export type PortalTaskScope = 'client' | 'vendor';

const tasksPath = (scope: PortalTaskScope, eventId: string) =>
  `/portal/${scope}/events/${eventId}/tasks`;

/** The caller's tasks across every event they are on, each carrying its event. */
export async function listMyPortalTasks(): Promise<ClientTask[]> {
  const { data } = await apiClient.get<ClientTask[]>('/portal/tasks');
  return data;
}

export async function listClientTasks(
  eventId: string,
  scope: PortalTaskScope = 'client',
): Promise<ClientTask[]> {
  const { data } = await apiClient.get<ClientTask[]>(tasksPath(scope, eventId));
  return data;
}

export async function createClientTask(
  eventId: string,
  input: CreateClientTaskInput,
  scope: PortalTaskScope = 'client',
): Promise<ClientTask> {
  const { data } = await apiClient.post<ClientTask>(tasksPath(scope, eventId), input);
  return data;
}

export async function toggleClientTask(
  eventId: string,
  taskId: string,
  isDone: boolean,
  scope: PortalTaskScope = 'client',
): Promise<ClientTask> {
  const { data } = await apiClient.post<ClientTask>(
    `${tasksPath(scope, eventId)}/${taskId}/toggle`,
    { isDone },
  );
  return data;
}

export async function updateClientTask(
  eventId: string,
  taskId: string,
  input: UpdateClientTaskInput,
  scope: PortalTaskScope = 'client',
): Promise<ClientTask> {
  const { data } = await apiClient.patch<ClientTask>(
    `${tasksPath(scope, eventId)}/${taskId}`,
    input,
  );
  return data;
}

export async function deleteClientTask(
  eventId: string,
  taskId: string,
  scope: PortalTaskScope = 'client',
): Promise<void> {
  await apiClient.delete(`${tasksPath(scope, eventId)}/${taskId}`);
}

export interface ClientQuestionnaire {
  id: string;
  title: string;
  description: string | null;
  status: QuestionnaireStatus;
  sentAt: string | null;
  submittedAt: string | null;
  /** Token for the shared fill flow (same as the emailed public link). */
  accessToken: string;
  questionCount: number;
  answeredCount: number;
}

export async function getClientEventQuestionnaires(
  eventId: string,
): Promise<ClientQuestionnaire[]> {
  const { data } = await apiClient.get<ClientQuestionnaire[]>(
    `/portal/client/events/${eventId}/questionnaires`,
  );
  return data;
}
